import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { LEGAL_CONTENT, EFFECTIVE } from "@/legalContent";
import { Logo } from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";

export default function LegalPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const doc = LEGAL_CONTENT[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (doc) document.title = `${doc.title} | FundAI`;
  }, [slug, doc]);

  if (!doc) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-zinc-400">Page not found.</p>
        <Link to="/" className="text-[#00D1FF]">Return home</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050507]">
      <header className="glass border-b border-white/5 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Logo />
          <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="legal-back-btn"><ArrowLeft className="h-4 w-4" /> Back</button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00D1FF]">Legal</span>
          <h1 className="font-head text-4xl md:text-5xl font-light tracking-tight text-metallic mt-4">{doc.title}</h1>
          <p className="mt-3 font-mono text-xs text-zinc-600">{EFFECTIVE}</p>
          <p className="mt-8 text-base md:text-lg font-light text-zinc-400 leading-relaxed">{doc.intro}</p>
        </motion.div>

        <div className="mt-12 space-y-10">
          {doc.sections.map((s, i) => (
            <div key={i} data-testid={`legal-section-${i}`}>
              <h2 className="font-head text-xl md:text-2xl font-medium flex gap-3"><span className="font-mono text-sm text-zinc-700 mt-1.5">{String(i + 1).padStart(2, "0")}</span>{s.h}</h2>
              <div className="mt-3 space-y-3 pl-0 md:pl-9">
                {s.p.map((para, j) => <p key={j} className="text-sm md:text-base text-zinc-400 leading-relaxed">{para}</p>)}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap gap-4">
          {Object.entries(LEGAL_CONTENT).filter(([k]) => k !== slug).map(([k, v]) => (
            <Link key={k} to={`/legal/${k}`} className="text-sm text-zinc-500 hover:text-[#00D1FF] transition-colors">{v.title}</Link>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
